/**
 * dragSortList — the pure math and shared markup conventions behind every plain
 * mouse-driven "drag a card to reorder this vertical list" gesture in the app: the Insights
 * panel (panels/InsightsPanel.tsx), the Triage panel (panels/TriagePanel.tsx), the standalone
 * Insights page (pages/InsightsPage.tsx), and the Plan page's own Plan/Triage list columns
 * (pages/PlanPage.tsx + pages/PlanBoardColumn.tsx) — all previously reimplemented this from
 * scratch with only cosmetic differences. See hooks/useDragSortList.ts for the stateful
 * mousedown/mousemove/mouseup wiring built on top of the pure functions here; that hook is used
 * as-is by the three simple list panels above, while PlanPage's own drag gesture (which can also
 * end on the Timeline canvas, not just reorder its origin list) only reuses these pure pieces —
 * see PlanPage.tsx's computeListInsertIndexFromY and PlanBoardColumn.tsx's own translateY usage.
 */
import React from 'react';

/** `data-*` attribute every draggable row tags itself with, holding its own display index —
 * the one thing all of this module's DOM-querying functions key off of. Kept as a single named
 * constant rather than a per-caller string (`data-insight-index`, `data-triage-index`, ...) so
 * every list actually shares one convention instead of merely resembling each other. */
export const DRAG_SORT_INDEX_ATTR = 'data-drag-sort-index';

/** One rendered row's vertical extent, as read from `getBoundingClientRect()` — everything
 * `getDragSortTargetIndex` needs to decide whether the pointer has passed it. */
export interface DragSortSiblingRect {
    index: number;
    top: number;
    height: number;
}

/** Snapshot of an in-progress list drag — `fromIndex` never changes once the drag starts;
 * `toIndex`/`mouseY` update on every mousemove (see useDragSortList). */
export interface DragSortState {
    fromIndex: number;
    toIndex: number;
    mouseY: number;
    offsetY: number;
    cardHeight: number;
    ghostX: number;
    ghostWidth: number;
}

/**
 * Index (among the OTHER rows actually rendered) the dragged item would land at if dropped at
 * `pointerY` — counts how many siblings' vertical midpoints the cursor has passed, clamped to a
 * valid index within the full list. `otherItems` should already exclude the dragged row itself
 * (its own slot doesn't count as something to "pass"). Pure/exported so it's unit-testable
 * without a real drag or a mounted list.
 */
export function getDragSortTargetIndex(
    pointerY: number,
    otherItems: DragSortSiblingRect[],
    totalItems: number,
): number {
    const passedCount = otherItems.reduce((count, item) => {
        const mid = item.top + item.height / 2;
        return count + (pointerY > mid ? 1 : 0);
    }, 0);

    return Math.max(0, Math.min(totalItems - 1, passedCount));
}

/**
 * Vertical offset (px) row `index` should be translated by while `state` is active, so the rows
 * sitting between the drag's origin and its current target slide out of the way of the dragged
 * (hidden, not removed — see the `is-hidden`/`draggable-list-item` CSS below) row, previewing
 * where it will land. `gap` is the list's own CSS `gap` between rows (e.g. `.insights-list`'s
 * 4px, `.plan-column__list`'s 8px) — it has to match exactly or the shifted rows over/undershoot
 * the dragged row's actual slot.
 */
export function getDragSortTranslateY(
    state: Pick<DragSortState, 'fromIndex' | 'toIndex' | 'cardHeight'> | null,
    index: number,
    gap: number,
): number {
    if (!state || state.fromIndex === state.toIndex) return 0;
    const { fromIndex, toIndex, cardHeight } = state;
    const shift = cardHeight + gap;
    if (toIndex > fromIndex && index > fromIndex && index <= toIndex) return -shift;
    if (toIndex < fromIndex && index >= toIndex && index < fromIndex) return shift;
    return 0;
}

/**
 * Class list for one draggable row, applying the shared `.draggable-list-item` slide/hide
 * transform CSS (app.css, "Drag-reorder pattern") on top of the caller's own row class.
 */
export function dragSortItemClassName(baseClassName: string, isDragActive: boolean, isBeingDragged: boolean): string {
    return `${baseClassName} draggable-list-item${isDragActive ? ' is-dragging' : ''}${isBeingDragged ? ' is-hidden' : ''}`;
}

/** `--translate-y` custom-property style for one row, or `undefined` when it has no shift to
 * apply — matches the other drag-sort call sites' "only set a style attribute when it does
 * something" convention. */
export function dragSortTranslateStyle(translateY: number): React.CSSProperties | undefined {
    return translateY !== 0 ? ({ '--translate-y': `${translateY}px` } as React.CSSProperties) : undefined;
}
